import React from 'react';
import { Button, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';

export interface SelfAssignButtonProps {
  /**
   * 選択値を設定するコールバック（単一選択用）
   * 呼び出し時、ログインユーザーのIDが渡される
   */
  onAssign?: (userId: string) => void;
  /**
   * 複数選択時のコールバック
   * 既存の選択値配列と現在のユーザーIDを受け取り、新しい配列を返す
   */
  onMultipleAssign?: (userId: string, currentValues: string[]) => string[];
  /** 現在の選択値（複数選択用、重複チェック用） */
  currentValues?: string[];
  /** ボタンを無効化 */
  disabled?: boolean;
  /** ツールチップテキスト */
  tooltip?: string;
  /** ボタンサイズ */
  size?: 'small' | 'middle' | 'large';
  /** カスタムスタイル */
  style?: React.CSSProperties;
}

/**
 * 「自分が担当」ボタンコンポーネント
 *
 * ユーザー選択プルダウンの隣に配置し、クリックで現在ログインしているユーザーを
 * プルダウンに自動設定する。
 *
 * @example
 * // 単一選択の場合
 * <SelfAssignButton
 *   onAssign={(userId) => form.setFieldValue('assignedUserId', userId)}
 * />
 *
 * @example
 * // 複数選択の場合
 * <SelfAssignButton
 *   currentValues={form.getFieldValue('assignedUserIds') || []}
 *   onMultipleAssign={(userId, current) => {
 *     if (current.includes(userId)) return current;
 *     return [...current, userId];
 *   }}
 *   onAssign={(userId) => {
 *     const current = form.getFieldValue('assignedUserIds') || [];
 *     if (!current.includes(userId)) {
 *       form.setFieldValue('assignedUserIds', [...current, userId]);
 *     }
 *   }}
 * />
 */
export const SelfAssignButton: React.FC<SelfAssignButtonProps> = ({
  onAssign,
  onMultipleAssign,
  currentValues = [],
  disabled = false,
  tooltip = '自分を担当に設定',
  size = 'middle',
  style,
}) => {
  const { user } = useAuthStore();

  const handleClick = () => {
    if (!user?.id) {
      console.warn('SelfAssignButton: ログインユーザーが見つかりません');
      return;
    }

    if (onMultipleAssign) {
      // 複数選択モードの場合は、onMultipleAssignを呼び出して
      // 呼び出し元で処理を行う
      onMultipleAssign(user.id, currentValues);
    }

    if (onAssign) {
      onAssign(user.id);
    }
  };

  // ログインしていない場合やユーザーIDがない場合は無効化
  const isDisabled = disabled || !user?.id;

  // 既に自分が選択されているかチェック
  const isAlreadyAssigned = user?.id ? currentValues.includes(user.id) : false;

  return (
    <Tooltip
      title={isAlreadyAssigned ? '既に担当に設定済みです' : tooltip}
    >
      <Button
        type={isAlreadyAssigned ? 'primary' : 'default'}
        shape="circle"
        icon={<UserOutlined />}
        onClick={handleClick}
        disabled={isDisabled}
        size={size}
        style={{
          ...style,
        }}
        aria-label={tooltip}
      />
    </Tooltip>
  );
};

export default SelfAssignButton;
